import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import Dropdown from "../Dropdown/Dropdown";
import EntityConstants from "../../configs/EntityConstants";
import { useTranslation } from "react-i18next";

export default function CreateTenantModal({
  isOpen,
  onClose,
  onTenantCreated,
  isEditMode = false,
  initialName = "",
  initialType = "",
  onUpdate,
}) {
  const { t } = useTranslation();

  const [name, setName] = useState(initialName);
  const [type, setType] = useState(initialType);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    type: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [touched, setTouched] = useState({
    name: false,

    type: false,
  });

  const maxCharsName = 100;

  const typeOptions = [
    { name: t("tenant:select_type"), value: "" },
    { name: t("tenant:institute"), value: "Institute" },
    { name: t("tenant:company"), value: "Company" },
    { name: t("tenant:university"), value: "University" },
  ];

  useEffect(() => {
    if (isEditMode) {
      setName(initialName);
      setType(initialType);
      setErrors({ name: "", type: "" });
      setErrorMessage("");
      setTouched({
        name: false,
        type: false,
      });
    } else {
      setName("");
      setType("");
      setErrors({
        name: "",
        type: "",
      });
      setTouched({
        name: false,
        type: false,
      });
      setErrorMessage("");
    }
  }, [isEditMode, initialName, initialType, isOpen]);

  const validateField = (fieldName, value) => {
    let error = "";

    if (fieldName === "name") {
      if (!value.trim()) {
        error = t("tenant:name_required");
      } else if (value.length > maxCharsName) {
        error = `${t("tenant:name_must_be")} ${maxCharsName} ${t(
          "tenant:characters_or_less",
        )}`;
      }
    }

    if (fieldName === "type") {
      if (!value.trim()) {
        error = t("tenant:type_required");
      }
    }

    return error;
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: "",
      type: "",
    };

    newErrors.name = validateField("name", name);
    newErrors.type = validateField("type", type);
    if (newErrors.name || newErrors.type) {
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxCharsName) {
      setName(value);
      if (touched.name) {
        const error = validateField("name", value);
        setErrors({ ...errors, name: error });
      }
      setErrorMessage("");
    }
  };

  const handleTypeChange = (value) => {
    setType(value);
    if (touched.type) {
      const error = validateField("type", value);
      setErrors({ ...errors, type: error });
    }
    setErrorMessage("");
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    const error = validateField(field, field === "name" ? name : type);
    setErrors({ ...errors, [field]: error });
  };

  const handleSubmit = () => {
    setTouched({
      name: true,
      type: true,
    });

    if (!validateForm()) return;

    setIsLoading(true);
    setErrorMessage("");

    if (isEditMode) {
      if (typeof onUpdate !== "function") {
        setIsLoading(false);
        return;
      }
      onUpdate(name, type);
      setIsLoading(false);
      onClose();
      return;
    }

    const payload = {
      name: name.trim(),
      type: type.trim(),
    };

    POST(EntityConstants.POST_CREATE_TENANT, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          Toast({
            type: "success",
            title: t("tenant:success"),
            message: data.message || t("tenant:tenant_created_successfully"),
          });
          onTenantCreated();
          resetForm();
          onClose();
          return;
        }
        if (typeof data === "object" && !Array.isArray(data)) {
          const fieldErrors = {
            name: data.name?.[0] || "",
            type: data.type?.[0] || "",
          };

          if (fieldErrors.name || fieldErrors.type) {
            setErrors(fieldErrors);

            Toast({
              type: "error",
              title: t("tenant:validation_error"),
              message: fieldErrors.name || fieldErrors.code || fieldErrors.type,
            });

            return;
          }
        }

        const genericMessage =
          data.error || data.message || t("tenant:failed_create_tenant_retry");

        setErrorMessage(genericMessage);

        Toast({
          type: "error",
          title: t("tenant:error"),
          message: genericMessage,
        });
      })
      .catch((error) => {
        console.error("Create tenant error:", error);
        const genericMessage = t("tenant:failed_create_tenant");
        setErrorMessage(genericMessage);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: genericMessage,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    setName("");
    setType("");
    setErrors({
      name: "",
      type: "",
    });
    setTouched({
      name: false,
      type: false,
    });
    setErrorMessage("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getSelectedType = () => {
    return (
      typeOptions.find((option) => option.value === type) || typeOptions[0]
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="create-tenant-modal"
                className="max-w-xl transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all w-full relative"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  {/* Close (X) Button at top right */}
                  <button
                    type="button"
                    id="create-tenant-modal-close"
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <div className="">
                    {" "}
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-cfHeadingText"
                    >
                      {isEditMode ? t("tenant:edit") : t("tenant:create_new")}{" "}
                      {t("tenant:tenant")}
                    </Dialog.Title>
                    <p className="text-sm text-cfGlobalText mt-1">
                      {isEditMode
                        ? t("tenant:edit_tenant_details_manage_settings")
                        : t("tenant:create_new_tenant_manage_settings")}
                    </p>
                    <div className="space-y-0 mt-6">
                      <div>
                        <label
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                          htmlFor="create-tenant-modal-dropdown-type-trigger"
                        >
                          {t("tenant:type")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div
                          className={`border ${
                            errors.type && touched.type
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } rounded-lg`}
                          onBlur={() => handleBlur("type")}
                        >
                          <Dropdown
                            options={typeOptions}
                            selected={getSelectedType()}
                            onSelect={handleTypeChange}
                            testId="create-tenant-modal-dropdown-type"
                            buttonClass={`w-full justify-between ${
                              errors.type && touched.type
                                ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                : "border-cfIssuedScreenSearchBorder"
                            } hover:border-cfButton focus:border-cfButton`}
                            menuClass="z-50 w-full"
                          />
                        </div>
                        {errors.type && touched.type && (
                          <p
                            id="create-tenant-modal-error-type"
                            className="mt-1 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                          >
                            {errors.type}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          className="block mt-3 text-sm font-medium text-cfHeadingText"
                          htmlFor="create-tenant-modal-input-name"
                        >
                          {t("tenant:name")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="create-tenant-modal-input-name"
                          className={`bg-cfTextInputBorder border ${
                            errors.name && touched.name
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder="e.g. CredFlow Global"
                          value={name}
                          onChange={handleNameChange}
                          onBlur={() => handleBlur("name")}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.name && touched.name && (
                            <p
                              id="create-tenant-modal-error-name"
                              className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                            >
                              {errors.name}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsName - name.length}{" "}
                              {t("tenant:characters_left")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {errorMessage && (
                      <p
                        id="create-tenant-modal-error-message"
                        className="mt-4 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center"
                      >
                        <span className="mr-1 text-base leading-none">*</span>
                        {errorMessage}
                      </p>
                    )}
                  </div>

                  <div className="flex mt-8 justify-end gap-4">
                    <button
                      type="button"
                      id="create-tenant-modal-cancel"
                      className="px-4 py-2 text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      {t("tenant:cancel")}
                    </button>
                    <button
                      type="submit"
                      id="create-tenant-modal-submit"
                      className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {isEditMode
                            ? t("tenant:updating")
                            : t("tenant:creating")}
                        </span>
                      ) : (
                        `${
                          isEditMode ? t("tenant:update") : t("tenant:create")
                        } ${t("tenant:tenant")}`
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
